import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { ToolResultRendererProps } from "./index";

/**
 * Clean, minimal renderer for dataset arrays.
 */

interface DatasetItem {
  id?: number;
  name?: string;
  description?: string | null;
  dataset_type?: { name?: string } | null;
  dataset_type_id?: number;
  create_date?: string;
  visibility_level?: string;
  remote_dataset?: boolean;
}

interface DatasetRowProps {
  dataset: DatasetItem;
}

const DatasetRow: React.FC<DatasetRowProps> = ({ dataset }) => {
  const theme = useTheme();
  const isPrivate = dataset.visibility_level === "private";
  const isRemote = dataset.remote_dataset;
  const typeName = dataset.dataset_type?.name;

  return (
    <Box
      sx={{
        py: 1.25,
        px: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:last-child": { borderBottom: "none" },
        "&:hover": { bgcolor: theme.palette.action.hover },
        transition: "background-color 0.15s",
      }}
    >
      <Stack direction="row" alignItems="flex-start" gap={1.5}>
        {/* Icon */}
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "6px",
            bgcolor: theme.palette.primary.main + "15",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          <StorageIcon
            sx={{ fontSize: 16, color: theme.palette.primary.main }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: theme.palette.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {dataset.name || "Unnamed"}
            </Typography>
            {isRemote && (
              <CloudOutlinedIcon
                sx={{ fontSize: 14, color: theme.palette.text.disabled }}
              />
            )}
            {isPrivate && (
              <LockOutlinedIcon
                sx={{ fontSize: 14, color: theme.palette.text.disabled }}
              />
            )}
          </Stack>

          {/* Meta line */}
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: "block",
              mt: 0.25,
            }}
          >
            {[dataset.id && `#${dataset.id}`, typeName]
              .filter(Boolean)
              .join(" · ")}
          </Typography>

          {/* Description */}
          {dataset.description && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                mt: 0.5,
                lineHeight: 1.4,
              }}
            >
              {dataset.description}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export const DatasetArrayRenderer: React.FC<ToolResultRendererProps> = ({
  data,
}) => {
  const theme = useTheme();
  const datasets = data as DatasetItem[];

  if (!Array.isArray(datasets) || datasets.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No datasets found
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.grey[50],
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {datasets.length} {datasets.length === 1 ? "dataset" : "datasets"}
        </Typography>
      </Box>

      {/* List */}
      <Box>
        {datasets.slice(0, 50).map((dataset, index) => (
          <DatasetRow key={dataset.id || index} dataset={dataset} />
        ))}
      </Box>

      {datasets.length > 50 && (
        <Box sx={{ px: 1.5, py: 1, bgcolor: theme.palette.grey[50] }}>
          <Typography variant="caption" color="text.secondary">
            +{datasets.length - 50} more
          </Typography>
        </Box>
      )}
    </Box>
  );
};
